import { Switch, Textarea } from "@xola/ui-kit";
import { useDebounceEffect } from "ahooks";
import { FormField, InputField, isValidEmail } from "common";
import { useField, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import { useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { useRecipientRules } from "./hooks/useGiftDetailsRequirements";
import { useCustomerDetailsStore } from "./store/customerDetailsStore";
import { InfoField } from "@/components/PurchaseCreate/InfoField";

export interface FormValues {
    recipientName: string;
    recipientEmail: string;
    message: string;
    code: string;
}

const SKIPPABLE_RECIPIENT_FIELDS = ["recipientName", "recipientEmail", "message"] as const;

export const CreatePurchasePersonalizeGiftFields = () => {
    const purchase = useCreatePurchaseStore((state) => state.purchase);
    const setPurchase = useCreatePurchaseStore((state) => state.actions.setPurchase);
    const activeItemIndex = useCreatePurchaseStore((state) => state.activeItemIndex);
    const codeRequired = useCreatePurchaseStore((state) => state.codeRequired);
    const isScannedGiftCardItem = useCreatePurchaseStore((state) => {
        return state.purchase.items[state.activeItemIndex]?.isScannedGiftCard;
    });
    const { isRecipientShown, isSkippable, isSkipped, isRecipientRequired } = useRecipientRules();

    const helpers = useField("code")[2];

    const { values, setFieldValue, setFieldError, setFieldTouched } = useFormikContext<FormValues>();
    // Copied on blur rather than per keystroke so the organizer fields never race the recipient while typing.
    const copyToOrganizer = (organizerField: "customerName" | "customerEmail") => (value: string) => {
        if (!useCustomerDetailsStore.getState().isSameAsRecipient) {
            return;
        }

        setFieldValue(organizerField, value, false);
        useCustomerDetailsStore.setState((state) => {
            state[organizerField] = value;
        });
    };

    const [sendToRecipient, setSendToRecipient] = useState(purchase.items[activeItemIndex].sendToRecipient ?? false);

    useDebounceEffect(
        () => {
            const recipientName = values.recipientName || purchase.items[activeItemIndex].recipientName;
            const recipientEmail = values.recipientEmail || purchase.items[activeItemIndex].recipientEmail;
            const message = values.message || purchase.items[activeItemIndex].message;

            setPurchase((draft) => {
                draft.items[activeItemIndex].recipientName = recipientName;
                draft.items[activeItemIndex].recipientEmail = recipientEmail;
                draft.items[activeItemIndex].message = message;
            });
        },
        [values.recipientName, values.recipientEmail, values.message],
        { leading: true, trailing: true },
    );

    const hasRecipientEmail = !!values.recipientEmail;
    // Emailing the certificate needs an address, so it can be turned on only once a valid email is entered.
    const canEmailCertificate = isValidEmail(values.recipientEmail ?? "");
    const isSendToRecipientDisabled = isSkipped || (!sendToRecipient && !canEmailCertificate);

    useEffect(() => {
        if (sendToRecipient && !hasRecipientEmail) {
            setSendToRecipient(false);
            setPurchase((draft) => {
                draft.items[activeItemIndex].sendToRecipient = false;
            });
        }
    }, [sendToRecipient, hasRecipientEmail, activeItemIndex, setPurchase]);

    const toggleSetSendToRecipient = (value: boolean) => {
        setSendToRecipient(value);

        setPurchase((draft) => {
            draft.items[activeItemIndex].sendToRecipient = value;
        });
    };

    useEffect(() => {
        const code = values.code || purchase.items[activeItemIndex].code;

        setPurchase((draft) => {
            draft.items[activeItemIndex].code = code;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.code]);

    const handleSkipToggle = (shouldSkip: boolean) => {
        if (shouldSkip) {
            // The schema still requires these until the store update re-renders, so clear without validating.
            for (const field of SKIPPABLE_RECIPIENT_FIELDS) {
                setFieldValue(field, "", false);
                setFieldTouched(field, false, false);
                setFieldError(field, undefined);
            }
            setSendToRecipient(false);
        }

        setPurchase((draft) => {
            const item = draft.items[activeItemIndex];
            item.isRecipientSkipped = shouldSkip;
            if (shouldSkip) {
                item.recipientName = "";
                item.recipientEmail = "";
                item.message = "";
                item.sendToRecipient = false;
            }
        });
    };

    const handleCodeToggle = (codeRequired: boolean) => {
        useCreatePurchaseStore.setState({ codeRequired });

        if (!codeRequired) {
            helpers.setValue("");
            setPurchase((draft) => {
                delete draft.items[activeItemIndex].code;
            });
        }
    };

    return (
        <>
            {isRecipientShown && (
                <>
                    <div className="flex items-center space-x-4">
                        <h3>Personalize Gift</h3>
                        {isSkippable && (
                            <Switch.Group>
                                <Switch
                                    size="small"
                                    isChecked={isSkipped}
                                    data-testid="personalize-gift-skip"
                                    onChange={handleSkipToggle}
                                />
                                <Switch.Label className="ml-2 text-gray-darker" direction="left">
                                    Skip
                                </Switch.Label>
                            </Switch.Group>
                        )}
                    </div>

                    <FormField name="recipientName" label="Recipient Name">
                        <InputField
                            isRequired={isRecipientRequired}
                            disabled={isSkipped}
                            onChange={copyToOrganizer("customerName")}
                        />
                    </FormField>

                    <FormField name="recipientEmail" label="Recipient Email">
                        <InputField
                            isRequired={isRecipientRequired}
                            disabled={isSkipped}
                            onChange={copyToOrganizer("customerEmail")}
                        />
                    </FormField>

                    <div className="mt-2">
                        <Switch.Group>
                            <Switch
                                size="small"
                                isChecked={sendToRecipient}
                                disabled={isSendToRecipientDisabled}
                                onChange={toggleSetSendToRecipient}
                            />
                            <Switch.Label className="ml-2 text-gray-darker" direction="left">
                                Email gift certificate to recipient
                            </Switch.Label>
                        </Switch.Group>
                    </div>

                    <FormField name="message" label="Message to Recipient">
                        <InputField as={Textarea} disabled={isSkipped} />
                    </FormField>
                </>
            )}

            <InfoField
                className="uppercase"
                isDisabled={!!isScannedGiftCardItem}
                name="code"
                label="Enter Custom Gift Code"
                isRequired={codeRequired}
                onToggleRequired={handleCodeToggle}
            />
        </>
    );
};
